/**
 * Asset Browser View
 *
 * Main view for browsing and managing media assets within a selected media type.
 */

import type { IAsset, ProviderRegistry } from '../providers';
import { useMediaAssets, useAssetMetadata, useAssetSelection } from '../hooks';
import { MediaHeader } from './MediaHeader';
import { MediaSidebar } from './MediaSidebar';
import { MediaGrid } from './MediaGrid';
import { MediaTabs } from './MediaTabs';
import { AssetDetailPanel } from './AssetDetailPanel';
import { useState, useEffect } from 'react';
import { InfiniteScrollContainer } from './InfiniteScrollContainer';
import { DragSelectContainer } from './DragSelectContainer';
import { SortOption, AssetSource, MediaType, AssetSelectCallback, Filters } from '../types';
import { Dropzone } from './Dropzone';

import { EmptyStateSection } from './EmptyStateSection';
import { ProcessingIcon } from '../../../icons';

export interface AssetBrowserViewProps {
  /** Provider registry containing all available asset providers */
  registry: ProviderRegistry;
  /** Current media type being browsed */
  mediaType?: MediaType | null;
  /** Available media types to display in the MediaTabs */
  enabledMediaTypes?: MediaType[];
  /** Workspace ID for filtering ZML assets */
  workspaceId?: string;
  /** Enable sidebar */
  enableNav?: boolean;
  /** Enable multi-selection of assets */
  enableMultiSelect?: boolean;
  /** Whether to show the multi-select toggle in the header */
  showMultiSelectToggle?: boolean;
  /** Callback to toggle multi-select mode */
  onToggleMultiSelect?: () => void;
  /** Initial asset source to display (for CMS mode navigation) */
  initialSource?: AssetSource;
  /** Initial search query from parent */
  initialSearchQuery?: string;
  /** Callback when search query changes */
  onSearchChange?: (query: string) => void;
  /** Sort option from parent */
  sortBy: SortOption;
  /** Callback when sort changes */
  onSortChange?: (sort: SortOption) => void;
  /** Filters from parent */
  filters?: Filters;
  /** Callback when filters change */
  onFiltersChange?: (filters: Filters) => void;
  /** Callback when an asset is selected */
  onSelect?: AssetSelectCallback;
  /** Callback for back navigation */
  onBack?: () => void;
  /** Callback for source selection */
  onSourceSelect?: (source: AssetSource) => void;
  /** Callback to handle asset editing */
  onEdit?: (asset: IAsset) => void;
  /** Callback to handle asset restoration */
  onRestore?: (assetId: string) => Promise<void>;
  /** Callback to delete a single asset */
  onDelete?: (assetId: string, asset: IAsset) => Promise<boolean>;
  /** Callback to delete multiple assets */
  onDeleteMultiple?: (assetIds: string[]) => Promise<boolean>;
  /** Callback to handle file uploads */
  handleUpload?: (files: FileList) => void;
  onOpenPremiumUploadModal?: () => void;
  /** Notifies the parent when the active media-type tab changes (the filter tab, or the type the browser was opened with). */
  onActiveMediaTypeChange?: (mediaType: MediaType | null) => void;
  /** Changing this value triggers a re-fetch of assets */
  refreshKey?: number;
  /** Custom CSS class */
  className?: string;
  setRefreshKey?: React.Dispatch<React.SetStateAction<number>>;
}

export const AssetBrowserView = ({
  registry,
  mediaType,
  workspaceId,
  enableNav,
  enableMultiSelect = true,
  showMultiSelectToggle = false,
  onToggleMultiSelect,
  initialSource = 'user-library',
  initialSearchQuery = '',
  onSearchChange,
  sortBy: sortByProp,
  onSortChange,
  filters: filtersProp,
  onFiltersChange,
  onSelect,
  onBack,
  onSourceSelect,
  onEdit,
  onRestore,
  onDelete,
  onDeleteMultiple,
  onOpenPremiumUploadModal,
  handleUpload,
  onActiveMediaTypeChange,
  className = '',
  enabledMediaTypes,
  refreshKey,
  setRefreshKey,
}: AssetBrowserViewProps) => {
  const [activeSource, setActiveSource] = useState<AssetSource>(initialSource);

  // Sync internal state when the parent changes the initial source (e.g. from an external navigation event)
  useEffect(() => {
    setActiveSource(initialSource);
  }, [initialSource]);
  const [searchQuery, setSearchQuery] = useState<string>(initialSearchQuery);
  const sortBy = sortByProp;
  const filters: Filters = filtersProp ?? { animated: [], templateType: [], status: [], labels: [] };
  const [filterMediaType, setFilterMediaType] = useState<MediaType | null>(null);
  const [isTableView, setIsTableView] = useState<boolean>(false);
  const isBinView = initialSource === 'trash';

  // Surface the active media-type tab (the filter tab, or the type the browser was
  // opened with) to the parent, e.g. so it can classify uploads in a 360 context.
  useEffect(() => {
    onActiveMediaTypeChange?.(filterMediaType ?? mediaType ?? null);
  }, [filterMediaType, mediaType, onActiveMediaTypeChange]);

  const { assets, loading, loadingMore, hasMore, loadMore, error, updateAssetInList } = useMediaAssets({
    registry,
    mediaType: filterMediaType || enabledMediaTypes || undefined,
    workspaceId,
    source: activeSource,
    searchQuery,
    sortBy,
    filters,
    refreshKey,
  });

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    onSearchChange?.(query);
  };

  const { hasAnimated: computedShowAnimatedFilter } = useAssetMetadata(assets);

  const handleAssetClick = (asset: IAsset) => {
    if (onSelect) {
      onSelect(asset);
    }
  };

  const handleDeleteAsset = async (assetId: string, asset: IAsset) => {
    const success = await onDelete?.(assetId, asset);
    if (success) clearSelection();
  };

  const handleDeleteMultiple = async (assetIds: string[]) => {
    const success = await onDeleteMultiple?.(assetIds);
    if (success) clearSelection();
  };

  const handleRestore = async (assetId: string) => {
    await onRestore?.(assetId);
    clearSelection();
  };

  const handleSourceChange = (source: AssetSource) => {
    setActiveSource(source);
    if (onSourceSelect) {
      onSourceSelect(source);
    }
  };

  const {
    selectedAssetIds,
    selectedAssets: selectedAssetObjects,
    hiddenSelectedCount,
    toggleSelection,
    addToSelection,
    clearSelection,
  } = useAssetSelection<IAsset>({
    enableMultiSelect,
    visibleAssets: assets,
  });


  return (
    <div className={`library-view ${enableNav ? 'library-view--with-sidebar' : ''} ${className}`}>
      {enableNav && (
        <div className="library-view__sidebar">
          <MediaSidebar
            registry={registry}
            mediaType={mediaType}
            useMediaTypeAstitle={!!mediaType && enabledMediaTypes?.length === 1}
            showWorkspaceAssets={!!workspaceId}
            activeSource={activeSource}
            onSourceChange={handleSourceChange}
            onBack={onBack}
          />
        </div>
      )}

      <div className="library-view__main">

        <div className="library-view__header">
          <MediaHeader
            searchQuery={searchQuery}
            sortBy={sortBy}
            filters={filters}
            onSearchChange={handleSearchChange}
            onSortChange={(s) => onSortChange?.(s)}
            onFiltersChange={(f) => onFiltersChange?.(f)}
            onUpload={handleUpload}
            onViewToggle={(isTableView) => setIsTableView(isTableView)}
            isTableView={isTableView}
            acceptedFileTypes={mediaType ? [mediaType] : enabledMediaTypes}
            showAnimatedFilter={computedShowAnimatedFilter || filters.animated[0] === 'true'}
            showMultiSelectToggle={showMultiSelectToggle}
            enableMultiSelect={enableMultiSelect}
            onToggleMultiSelect={onToggleMultiSelect}
            onOpenPremiumUploadModal={onOpenPremiumUploadModal}
          />
        </div>

        {enabledMediaTypes && enabledMediaTypes?.length > 1 && !isBinView && (
          <div className="library-view__tabs">
            <MediaTabs
              mediaTypes={enabledMediaTypes || Object.values(MediaType).filter(type => type !== MediaType.Unknown) as MediaType[]}
              activeType={filterMediaType ?? mediaType ?? null}
              onTabChange={setFilterMediaType}
            />
          </div>
        )}

        {isBinView && (
          <div className="library-view__title"><h2>Bin {assets.length > 0 ? `(${assets.length})` : ''}</h2></div>
        )}

        <DragSelectContainer enabled={enableMultiSelect} onSelect={toggleSelection} onSelectMany={addToSelection}>
          {({ dragHighlightedIds, isDragging }) => (
            <Dropzone
              onUpload={handleUpload}
            >
              <div className="library-view__content">
                {loading ? (
                  <div className="library-view__loading">
                    <ProcessingIcon />
                  </div>
                ) : assets.length === 0 ? (
                  <EmptyStateSection
                    sectionTitle={activeSource === 'trash' ? 'Bin' : activeSource === 'user-library' ? 'Personal (legacy)' : activeSource === 'workspace-library' ? 'Workspace' : 'Explore'}
                    tab={activeSource !== 'trash' && filterMediaType ? `${filterMediaType}` : ''}
                    searchQuery={searchQuery}
                    onUpload={activeSource === 'workspace-library' ? handleUpload : undefined}
                    onOpenPremiumUploadModal={onOpenPremiumUploadModal}
                    acceptedFileTypes={mediaType ? [mediaType] : enabledMediaTypes}
                  />
                ) : (
                  <InfiniteScrollContainer
                    onLoadMore={loadMore}
                    hasMore={hasMore}
                    loadingMore={loadingMore}
                    loader={<div className="library-view__loading"><ProcessingIcon /></div>}
                  >
                    <MediaGrid
                      assets={assets}
                      selectedAssets={selectedAssetIds}
                      onAssetClick={handleAssetClick}
                      onAssetSelect={toggleSelection}
                      loading={loading}
                      error={error}
                      isTableView={isTableView}
                      enableMultiSelect={enableMultiSelect}
                      dragHighlightedIds={dragHighlightedIds}
                      isDragActive={isDragging}
                      className={className}
                      key={refreshKey}
                    />
                  </InfiniteScrollContainer>
                )}
              </div>
            </Dropzone>
          )}
        </DragSelectContainer>
      </div>

      <div className="library-view__detail">
        <AssetDetailPanel
          selectedAssets={selectedAssetObjects}
          selectedAssetIds={selectedAssetIds}
          totalSelectedCount={selectedAssetIds.length}
          hiddenSelectedCount={hiddenSelectedCount}
          activeSource={activeSource}
          onDelete={handleDeleteAsset}
          onDeleteMultiple={handleDeleteMultiple}
          onEdit={onEdit}
          onRestore={handleRestore}
          onDeselectAll={clearSelection}
          key={selectedAssetIds.join(',')} // Force remount when selection changes to reset internal state
          updateAssetInList={updateAssetInList}
          setRefreshKey={setRefreshKey}
        />
      </div>
    </div>
  );
};
